/**
 * Image validation and processing utilities
 */

/** Image CDN base URL */
const IMAGE_CDN_BASE = 'https://images.hadv.quest'

/**
 * Get the full URL for an image, ensuring it uses the correct CDN domain
 * @param url - The image URL (can be relative, absolute, or full URL)
 * @returns Full URL with correct domain, or null if input is null/undefined
 */
export function getImageUrl(url: string | null | undefined): string | null {
  if (!url) return null

  // Already a full URL with correct domain
  if (url.startsWith(IMAGE_CDN_BASE)) {
    return url
  }

  // Already a data URL (base64)
  if (url.startsWith('data:')) {
    return url
  }

  // Full URL with different domain - replace with our CDN
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const urlObj = new URL(url)
      // Extract the path and use our CDN
      return `${IMAGE_CDN_BASE}${urlObj.pathname}`
    } catch {
      // Invalid URL, return as-is
      return url
    }
  }

  // Relative URL - prepend CDN base
  if (url.startsWith('/')) {
    return `${IMAGE_CDN_BASE}${url}`
  }

  // Path without leading slash
  return `${IMAGE_CDN_BASE}/${url}`
}

export interface ImageValidationResult {
  valid: boolean
  error?: string
}

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

/**
 * Validate an image file based on size and type constraints
 * @param file - The file to validate
 * @param maxSizeMB - Maximum file size in megabytes
 * @returns Validation result with error message if invalid
 */
export function validateImageFile(
  file: File,
  maxSizeMB: number = 5
): ImageValidationResult {
  // Check if file exists
  if (!file) {
    return { valid: false, error: 'No file provided' }
  }

  // Check file type
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.',
    }
  }

  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit. Please choose a smaller image.`,
    }
  }

  return { valid: true }
}

/**
 * Create a data URL preview from an image file
 * @param file - The image file to preview
 * @returns Promise resolving to data URL string
 */
export function createImagePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'))
      return
    }

    const reader = new FileReader()

    reader.onload = (e) => {
      const result = e.target?.result
      if (typeof result === 'string') {
        resolve(result)
      } else {
        reject(new Error('Failed to read file as data URL'))
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * Get a human-readable file size string
 * @param bytes - File size in bytes
 * @returns Formatted string like "2.5 MB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Check if a file is an image based on its type
 * @param file - The file to check
 * @returns True if the file is an image
 */
export function isImageFile(file: File): boolean {
  return ACCEPTED_IMAGE_TYPES.includes(file.type)
}

/**
 * Get file extension from filename
 * @param filename - The filename
 * @returns File extension (e.g., "jpg", "png")
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

/**
 * Image compression options
 */
export interface CompressImageOptions {
  maxWidth?: number   // Maximum width in pixels (default: 600)
  maxHeight?: number  // Maximum height in pixels (default: 600)
  quality?: number    // JPEG quality 0-1 (default: 0.8)
}

/**
 * Check if an image has transparency by examining pixel data
 * @param canvas - Canvas with the image drawn on it
 * @param ctx - Canvas 2D context
 * @returns True if image has any transparent pixels
 */
function hasTransparency(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): boolean {
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  // Check alpha channel (every 4th value starting at index 3)
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) {
      return true
    }
  }
  return false
}

/**
 * Compress an image file and return as base64 data URL
 * - Resizes to fit within maxWidth/maxHeight while maintaining aspect ratio
 * - Preserves PNG format for images with transparency
 * - Converts to JPEG for opaque images (better compression)
 *
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise resolving to compressed base64 data URL
 */
export function compressImage(
  file: File,
  options: CompressImageOptions = {}
): Promise<string> {
  const { maxWidth = 600, maxHeight = 600, quality = 0.8 } = options

  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'))
      return
    }

    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      const dataUrl = e.target?.result as string
      img.src = dataUrl
    }

    reader.onerror = () => {
      reject(new Error('Failed to read image file'))
    }

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img

      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height

        if (width > height) {
          width = maxWidth
          height = Math.round(maxWidth / aspectRatio)
        } else {
          height = maxHeight
          width = Math.round(maxHeight * aspectRatio)
        }

        // Ensure we don't exceed either dimension
        if (width > maxWidth) {
          width = maxWidth
          height = Math.round(maxWidth / aspectRatio)
        }
        if (height > maxHeight) {
          height = maxHeight
          width = Math.round(maxHeight * aspectRatio)
        }
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      // Draw image to canvas
      ctx.drawImage(img, 0, 0, width, height)

      // Check for transparency to determine output format
      const isPng = file.type === 'image/png'
      const needsTransparency = isPng && hasTransparency(canvas, ctx)

      // Export as PNG if transparency needed, otherwise JPEG for better compression
      let outputDataUrl: string
      if (needsTransparency) {
        outputDataUrl = canvas.toDataURL('image/png')
      } else {
        outputDataUrl = canvas.toDataURL('image/jpeg', quality)
      }

      resolve(outputDataUrl)
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * Compress a base64 data URL image
 * Useful for images already in data URL format (e.g., from clipboard)
 *
 * @param dataUrl - The base64 data URL to compress
 * @param options - Compression options
 * @returns Promise resolving to compressed base64 data URL
 */
export function compressDataUrl(
  dataUrl: string,
  options: CompressImageOptions = {}
): Promise<string> {
  const { maxWidth = 600, maxHeight = 600, quality = 0.8 } = options

  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img

      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height

        if (width > height) {
          width = maxWidth
          height = Math.round(maxWidth / aspectRatio)
        } else {
          height = maxHeight
          width = Math.round(maxHeight * aspectRatio)
        }

        // Ensure we don't exceed either dimension
        if (width > maxWidth) {
          width = maxWidth
          height = Math.round(maxWidth / aspectRatio)
        }
        if (height > maxHeight) {
          height = maxHeight
          width = Math.round(maxHeight * aspectRatio)
        }
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      // Draw image to canvas
      ctx.drawImage(img, 0, 0, width, height)

      // Check for transparency to determine output format
      const isPng = dataUrl.startsWith('data:image/png')
      const needsTransparency = isPng && hasTransparency(canvas, ctx)

      // Export as PNG if transparency needed, otherwise JPEG for better compression
      let outputDataUrl: string
      if (needsTransparency) {
        outputDataUrl = canvas.toDataURL('image/png')
      } else {
        outputDataUrl = canvas.toDataURL('image/jpeg', quality)
      }

      resolve(outputDataUrl)
    }

    img.onerror = () => {
      reject(new Error('Failed to load image from data URL'))
    }

    img.src = dataUrl
  })
}
